<template>
  <div class="password-requirements">
    <div class="password-requirements__strength">
      <div class="password-requirements__strength__label">
        {{ dict.get('online-Shop.Password.Validation.Strength.Label') }}
      </div>

      <div class="password-requirements__strength__bar">
        <span
          :class="{
            'password-requirements__strength__bar__indicator': true,
            'password-requirements__strength__bar__indicator--low': passwordStrength < 2,
            'password-requirements__strength__bar__indicator--mid':
              passwordStrength >= 2 && passwordStrength < 3,
            'password-requirements__strength__bar__indicator--high': passwordStrength >= 3,
          }"
          :style="{ maxWidth: strengthBarWidth + '%' }"
        />
      </div>
      <div class="password-requirements__strength__hint">{{ strengthHint }}</div>
    </div>

    <div class="password-requirements__requirements">
      <div class="password-requirements__requirement">
        <icon-fi
          :class="{
            'password-requirements__requirement__icon': true,
            'password-requirements__requirement__icon--success': passwordLengthCheck,
          }"
          :icon="passwordLengthCheck ? 'check-rounded-filled' : 'failed-rounded'"
        />
        {{ dict.get('online-Shop.Password.Validation.Password-Length-Check') }}
      </div>
      <div
        v-if="dict.get('online-Shop.Password.Validation.Description') !== ''"
        class="password-requirements__requirements__description"
      >
        {{ dict.get('online-Shop.Password.Validation.Description') }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import zxcvbn from 'zxcvbn';

import { IconFi } from '@/components/atoms/index';
import { useDict } from '@/composables/fischerPlugin';

const emits = defineEmits<{
  'valid-changed': [value: boolean];
}>();

const props = defineProps<{
  password: string;
}>();

const dict = useDict();

const passwordLengthCheck = computed(() => {
  return /^(?=.{8,})/.test(props.password) || false;
});

watch(passwordLengthCheck, (newValue, oldValue) => {
  if (newValue !== oldValue) {
    emits('valid-changed', newValue);
  }
});

const passwordStrength = computed(() => {
  if (passwordLengthCheck.value) {
    return zxcvbn(props.password)?.score;
  }

  return -1;
});

const strengthBarWidth = computed(() => {
  if (passwordStrength.value >= 0) {
    let strength = passwordStrength.value;
    if (passwordStrength.value === 0) {
      strength = 1;
    }

    // zxcvbn scores password strength from 0 to 4
    return (100 / 4) * strength;
  }

  return 0;
});

const strengthHint = computed(() => {
  if (props.password && props.password !== '') {
    switch (passwordStrength.value) {
      case 0:
      case 1:
        return dict.get('online-Shop.Password.Validation.Strength.Status.To-Simple');
      case 2:
        return dict.get('online-Shop.Password.Validation.Strength.Status.Simple');
      case 3:
        return dict.get('online-Shop.Password.Validation.Strength.Status.Good');
      case 4:
        return dict.get('online-Shop.Password.Validation.Strength.Status.Strong');
      default:
        return dict.get('online-Shop.Password.Validation.Strength.Status.Insufficient');
    }
  }

  return '';
});
</script>

<style src="./password-requirements" />
